import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { Box, Divider, Paper, Typography, Skeleton, SxProps, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Account } from 'types/Account';
import AccountAvatar from 'components/AccountAvatar';
import { maskTaxPayer, maskPhone, maskZipCode } from 'services/mask';
import { getNonNullishValue, nonNullish } from 'services/helper';

const ACCOUNT_DATA = gql`
  query AccountDataQuery($id: Int!, $masterCompanyId: Int, $isCloud: Boolean!) {
    AccountById(id: $id, masterCompanyId: $masterCompanyId, isCloud: $isCloud) {
      id
      companyName
      accountCode
      partitionNumber
      partitions {
        number
      }
      address
      zipCode
      city
      neighborhood
      taxPayerId
      responsible
      email
      phoneNumber1
      phoneNumber2
      vacation {
        startDate
        endDate
      }
    }
  }
`;

function AccountData({ accountSelected }: { accountSelected: Account }) {
  const { t } = useTranslation('_common');

  const { data, error } = useQuery<{ AccountById: Account }>(ACCOUNT_DATA, {
    variables: { ...accountSelected },
  });
  const account = data?.AccountById || undefined;

  if (error) {
    throw error;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('account')}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
          {account ? (
            <AccountAvatar
              image={accountSelected.custom?.image}
              selectedColor={accountSelected.custom?.color}
              accountName={accountSelected.displayName || t('not_registered')}
              size={40}
            />
          ) : (
            <Skeleton variant="circular" width={40} height={40} />
          )}

          <AccountDataItem
            label={t('account_name')}
            value={
              account ? (
                accountSelected.displayName || t('not_registered')
              ) : (
                <Skeleton variant="text" />
              )
            }
            sx={{ ml: 2 }}
          />
        </Grid>

        <Grid item xs={3}>
          <AccountDataItem
            label={t('account_code')}
            value={account ? account.accountCode : <Skeleton variant="text" />}
          />
        </Grid>

        <Grid item xs={3}>
          <AccountDataItem
            label={t('tax_payer_id')}
            value={
              account ? (
                account.taxPayerId ? (
                  maskTaxPayer(account.taxPayerId)
                ) : (
                  t('not_registered')
                )
              ) : (
                <Skeleton variant="text" />
              )
            }
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <AccountDataItem
            label={t('address')}
            value={
              account ? account.address?.trim() || t('not_registered') : <Skeleton variant="text" />
            }
            sx={{ flex: 2 }}
          />
        </Grid>

        <Grid item xs={3}>
          <AccountDataItem
            label={t('city')}
            value={
              account ? account.city?.trim() || t('not_registered') : <Skeleton variant="text" />
            }
          />
        </Grid>

        <Grid item xs={3}>
          <AccountDataItem
            label={t('zip_code')}
            value={
              account ? (
                account.zipCode ? (
                  maskZipCode(account.zipCode)
                ) : (
                  t('not_registered')
                )
              ) : (
                <Skeleton variant="text" />
              )
            }
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0, mb: 2 }}>
        <Grid item xs={6}>
          <AccountDataItem
            label={t('responsible')}
            value={
              account ? (
                (account.responsible ? getNonNullishValue(account.responsible?.trim()) : null) ||
                t('not_registered')
              ) : (
                <Skeleton variant="text" />
              )
            }
          />
        </Grid>

        <Grid item xs={6}>
          <AccountDataItem
            label={t('email')}
            value={account ? account.email || t('not_registered') : <Skeleton variant="text" />}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <AccountDataItem
            label={t('phones')}
            value={
              account ? (
                [account.phoneNumber1, account.phoneNumber2]
                  .filter(Boolean)
                  .filter(nonNullish)
                  .map((phone) => maskPhone(phone))
                  .join(', ') || t('not_registered')
              ) : (
                <Skeleton variant="text" />
              )
            }
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

function AccountDataItem({
  label,
  value,
  sx,
}: {
  label: string;
  value: string | JSX.Element;
  sx?: SxProps;
}) {
  return (
    <Box sx={sx}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography>{value}</Typography>
    </Box>
  );
}

export default AccountData;
